import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WheelLettersEditor from '../../mini-tasks/wheel/WheelLettersEditor';
import useDanduClassLive from '../../live/useDanduClassLive';
import { SSE_HTTP_FALLBACK_INTERVAL_MS } from '../../live/sseHttpFallbackPolicy';
import { fetchWheelSettings, fetchWheelPool, saveWheelSettings } from '../../mini-tasks/wheel/wheelApi';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import { getMeCached } from '../authCache';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import {
  dismissSmartboardMessage,
  fetchSmartboardMessageStatus,
  sendSmartboardMessage,
  fetchSmartboardTeacherPin,
  rotateSmartboardTeacherPin,
} from '../api/miniTasks';

const messageActionSx = {
  minHeight: 44,
  '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 2 },
};

export default function SmartboardPage() {
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [status, setStatus] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [messageOpen, setMessageOpen] = useState(false);
  const [teacherPin, setTeacherPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const requestVersion = useRef(0);
  const busyRef = useRef(false);
  const classMessage = status?.message_scope === 'class';
  const loadWheelSettings = useCallback(() => fetchWheelSettings(schoolId, classId), [schoolId, classId]);
  const loadWheelPool = useCallback(() => fetchWheelPool(schoolId, classId), [schoolId, classId]);
  const saveWheelLetters = useCallback((body) => saveWheelSettings(schoolId, classId, body), [schoolId, classId]);

  const selectedClass = useMemo(
    () => classes.find((item) => String(item.id) === String(classId)),
    [classId, classes],
  );
  const liveConnected = useDanduClassLive({
    enabled: Boolean(schoolId && classId),
    path: schoolId && classId
      ? `/admin/mini-tasks/${encodeURIComponent(schoolId)}/classes/${encodeURIComponent(classId)}/live/events?mode=status`
      : null,
    onSnapshot: (next) => {
      if (busyRef.current) return;
      setStatus(next);
      setError('');
    },
  });

  useEffect(() => {
    if (!schoolId || !classId) return;
    let active = true;
    setTeacherPin('');
    setPinError('');
    fetchSmartboardTeacherPin(schoolId, classId).then((result) => {
      if (active) setTeacherPin(result.pin);
    }).catch((err) => { if (active) setPinError(err.message); });
    return () => { active = false; };
  }, [schoolId, classId]);

  const renewPin = async () => {
    setPinBusy(true);
    setPinError('');
    try {
      const result = await rotateSmartboardTeacherPin(schoolId, classId);
      setTeacherPin(result.pin);
    } catch (err) { setPinError(err.message); }
    finally { setPinBusy(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const me = await getMeCached();
        const sid = me?.data?.admin_user?.school_id;
        if (!sid) throw new Error('Okul bilgisi bulunamadı.');
        const rows = await fetchClasses(sid);
        if (!active) return;
        const list = Array.isArray(rows) ? rows : [];
        setSchoolId(sid);
        setClasses(list);
        setClassId(list[0]?.id ? String(list[0].id) : '');
      } catch (err) {
        if (active) setError(err?.message || 'Akıllı tahta bilgileri yüklenemedi.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!schoolId || !classId) return undefined;
    let active = true;
    const refresh = async () => {
      if (busyRef.current) return;
      const version = requestVersion.current;
      try {
        const next = await fetchSmartboardMessageStatus(schoolId, classId);
        if (active && version === requestVersion.current && !busyRef.current) {
          setStatus(next);
          setError('');
        }
      } catch (err) {
        if (active && version === requestVersion.current && !busyRef.current) {
          setStatus(null);
          setError(err?.message || 'Akıllı tahta durumu alınamadı.');
        }
      }
    };
    if (!liveConnected) refresh();
    const timer = window.setInterval(() => {
      if (!liveConnected) refresh();
    }, SSE_HTTP_FALLBACK_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [schoolId, classId, liveConnected]);

  const refreshConflict = async (err, version) => {
    if (err?.status !== 409) return;
    try {
      const next = await fetchSmartboardMessageStatus(schoolId, classId);
      if (version === requestVersion.current) setStatus(next);
    } catch (_) { /* Preserve the original action error and allow the next refresh. */ }
  };

  const send = async () => {
    if (!status?.connected || (!classMessage && !status.board_session_id) || !text.trim()) return;
    const version = ++requestVersion.current;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await sendSmartboardMessage(schoolId, classId, classMessage ? undefined : status.board_session_id, text.trim());
      if (version !== requestVersion.current) return;
      setStatus({ ...status, message: result.message });
      setText('');
    } catch (err) {
      if (version !== requestVersion.current) return;
      setError(err?.message || 'Mesaj gönderilemedi.');
      await refreshConflict(err, version);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if ((!classMessage && !status?.board_session_id) || !status?.message?.id) return;
    const version = ++requestVersion.current;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await dismissSmartboardMessage(schoolId, classId, classMessage ? undefined : status.board_session_id, status.message.id);
      if (version !== requestVersion.current) return;
      setStatus({ ...status, message: null });
    } catch (err) {
      if (version !== requestVersion.current) return;
      setError(err?.message || 'Mesaj kaldırılamadı.');
      await refreshConflict(err, version);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 920 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Akıllı Tahta</Typography>
          <Typography sx={{ color: 'text.secondary' }}>Sınıfınız için akıllı tahta özelliklerini buradan yönetin.</Typography>
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2}>
            <TextField select label="Sınıf" value={classId} disabled={busy} onChange={(event) => {
              requestVersion.current += 1;
              setStatus(null);
              setText('');
              setError('');
              setMessageOpen(false);
              setClassId(event.target.value);
            }} size="small" sx={{ maxWidth: 360 }}>
              {classes.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name || item.code || item.id}</MenuItem>)}
            </TextField>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8f7ff' }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Öğretmen Modu PIN'i</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>Bu PIN yalnızca seçili sınıfın akıllı tahtasında geçerlidir.</Typography>
              {pinError && <Alert severity="info" sx={{ mb: 1 }}>{pinError}</Alert>}
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography sx={{ fontFamily: 'monospace', fontSize: 30, letterSpacing: 6, fontWeight: 900 }}>{teacherPin || '••••'}</Typography>
                <Button variant="outlined" onClick={renewPin} disabled={!teacherPin || pinBusy}>Yeni PIN Oluştur</Button>
              </Stack>
            </Paper>

            {error && <Alert severity="error">{error}</Alert>}
            {String(selectedClass?.grade_level) === '1' && <WheelLettersEditor key={`${schoolId}:${classId}`}
              loadSettings={loadWheelSettings} saveSettings={saveWheelLetters} loadPool={loadWheelPool} />}

            <Box
              component="button"
              type="button"
              onClick={() => setMessageOpen(true)}
              sx={{
                width: '100%', maxWidth: 360, textAlign: 'left', cursor: 'pointer', border: '1px solid',
                borderColor: status?.connected ? 'success.light' : 'divider', borderRadius: 2, p: 2,
                bgcolor: 'background.paper', font: 'inherit', color: 'inherit',
                '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.light' },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <DesktopWindowsRoundedIcon color={status?.connected ? 'success' : 'disabled'} sx={{ mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800 }}>Anlık Mesaj</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.35 }}>
                    {classMessage ? 'Sınıfın açık tahtalarında ortak mesaj gösterin.' : 'Öğretmenin mesajını sınıf tahtasında gösterin.'}
                  </Typography>
                  <Chip size="small" sx={{ mt: 1 }} color={status?.connected ? 'success' : 'default'} label={status?.connected ? 'Tahta bağlı' : 'Tahta bağlı değil'} />
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={messageOpen} onClose={() => !busy && setMessageOpen(false)} fullWidth maxWidth="sm" aria-labelledby="smartboard-message-title" aria-describedby={classMessage ? 'smartboard-message-description' : undefined}>
        <DialogTitle id="smartboard-message-title">Anlık Mesaj{selectedClass ? ` · ${selectedClass.name || selectedClass.code}` : ''}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {classMessage && <Typography id="smartboard-message-description" variant="body2" sx={{ color: 'text.secondary' }}>
              Bu mesaj, bu sınıfa ait tüm açık tahtalarda gösterilir. Kapatınca hepsinden kaldırılır.
            </Typography>}
            {!status?.connected && <Alert severity="info">Akıllı tahta açık ve bağlı olunca mesaj gönderebilirsiniz.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {status?.message ? (
              <>
                <Typography sx={{ fontWeight: 800 }}>{classMessage ? 'Sınıfın açık mesajı' : 'Tahtada gösterilen mesaj'}</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{status.message.text}</Typography>
                <Button disableFocusRipple variant="outlined" color="error" onClick={dismiss} disabled={busy} sx={{ ...messageActionSx, alignSelf: 'flex-start' }}>{classMessage ? 'Mesajı Tüm Tahtalarda Kapat' : 'Tahtadaki Mesajı Kapat'}</Button>
              </>
            ) : (
              <>
                <TextField label="Kısa mesaj" value={text} onChange={(event) => setText(event.target.value)} inputProps={{ maxLength: 240 }} multiline minRows={3} fullWidth disabled={!status?.connected || busy} />
                <Button disableFocusRipple variant="contained" onClick={send} disabled={!status?.connected || !text.trim() || busy} sx={{ ...messageActionSx, alignSelf: 'flex-start' }}>Mesajı Gönder</Button>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions><Button disableFocusRipple onClick={() => setMessageOpen(false)} disabled={busy} sx={messageActionSx}>Kapat</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
